import { getEnrollmentStatusLabel } from 'utils/enrollmentStatuses';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import xhr from 'xhr';
import {
  MONTHS_CATALOG,
  deriveSemesterNameFromDate,
  fetchAllSemesters,
  fetchAllStudentEnrollments,
  fetchMonthStudentsByEnrollment,
  saveMonths,
} from './useMonthStudent';

const TYPE_LABEL = {
  grade: 'Grado',
  technical_career: 'Técnica',
  course: 'Curso',
};

function enrollmentKey(type, id) {
  return `${type}:${id}`;
}

function Month({
  idStudent,
  type,                    // contexto (del padre) — sirve como default
  currentEnrollment,       // objeto enrollment ya cargado (form.js / UpdateMonthsModal)
  program,                 // { id, name? } — contexto para elegir el default
  currentProgramType,      // { id }
  paymentDate,             // precarga de semestre (fecha del pago)
  preselectedSemesterId,   // precarga de semestre (deep-link del reporte)
  lockProgram,             // NUEVO: si true, no muestra selector de programa (para + Pago)
  insert,                  // true → este componente maneja su propio botón Actualizar
  setIsOpen,               // cerrar el modal padre tras guardar
  onSaved,                 // callback tras guardar
  onStateChange,           // callback con { enrollmentId, enrollmentType, semesterId, months, onDay, graduationFeePaid }
  onDayMode,               // override opcional: 'on_day' | 'graduation_fee' | 'none'. Por defecto se deriva del
                           // program-type de la matrícula seleccionada: has_graduation_fee → 'graduation_fee', si no → 'none'.
  // Compat con parents que aún leen estos setters
  setMonths: setMonthsParent,
  setChecked: setCheckedParent,
  setMonthStudent: setMonthStudentParent,
  fetching: fetchingParent,
  monthsError,
  showRequiredLabel,
}) {
  const toasts = useToast();
  const { isOpen: isAddOpen, onOpen: openAdd, onClose: closeAdd } = useDisclosure();

  const [allEnrollments, setAllEnrollments] = useState([]);
  const [isLoadingEnrollments, setIsLoadingEnrollments] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [allMonthStudents, setAllMonthStudents] = useState([]);
  const [isLoadingMs, setIsLoadingMs] = useState(false);
  const [catalogSemesters, setCatalogSemesters] = useState([]);
  const [extraSemesters, setExtraSemesters] = useState([]);
  const [selectedSemesterId, setSelectedSemesterId] = useState(null);
  const [checkedMonths, setCheckedMonths] = useState([]);
  const [checkedOnDay, setCheckedOnDay] = useState(false);
  // "Al día en derechos de grado": vive en el enrollment (graduation_fee_paid), no en month-student.
  const [checkedGradFee, setCheckedGradFee] = useState(false);
  const [addPickerValue, setAddPickerValue] = useState('');
  const [updating, setUpdating] = useState(false);

  // Carga todas las matrículas del estudiante (una vez por studentId).
  useEffect(() => {
    if (!idStudent) return;
    let cancelled = false;
    (async () => {
      setIsLoadingEnrollments(true);
      try {
        const rows = await fetchAllStudentEnrollments(idStudent);
        if (!cancelled) setAllEnrollments(rows);
      } catch (err) {
        console.error('Month load enrollments:', err);
      } finally {
        if (!cancelled) setIsLoadingEnrollments(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idStudent]);

  // Elige la matrícula por defecto según el contexto (una sola vez).
  useEffect(() => {
    if (selectedKey !== null) return;
    if (allEnrollments.length === 0) return;

    // Prioridad: currentEnrollment > (type + program.id) > primera
    if (currentEnrollment?.id && type) {
      const match = allEnrollments.find(
        (e) => e.type === type && Number(e.enrollmentId) === Number(currentEnrollment.id)
      );
      if (match) { setSelectedKey(enrollmentKey(match.type, match.enrollmentId)); return; }
    }
    if (program?.id && type) {
      const match = allEnrollments.find(
        (e) => e.type === type && Number(e.program?.id) === Number(program.id)
      );
      if (match) { setSelectedKey(enrollmentKey(match.type, match.enrollmentId)); return; }
    }
    setSelectedKey(enrollmentKey(allEnrollments[0].type, allEnrollments[0].enrollmentId));
  }, [allEnrollments, currentEnrollment, program, type, selectedKey]);

  const selectedEnrollment = useMemo(() => {
    if (!selectedKey) return null;
    const [t, idStr] = selectedKey.split(':');
    return allEnrollments.find((e) => e.type === t && String(e.enrollmentId) === idStr) || null;
  }, [selectedKey, allEnrollments]);

  const enrollmentId = selectedEnrollment?.enrollmentId ?? null;
  const enrollmentType = selectedEnrollment?.type ?? null;
  const isSemesterBasedSelected = selectedEnrollment?.isSemesterBased === true;
  const hasGraduationFee = selectedEnrollment?.programType?.has_graduation_fee === true;
  const mode = onDayMode || (hasGraduationFee ? 'graduation_fee' : 'none');

  // Valor inicial del switch de derechos de grado = el guardado en la matrícula seleccionada.
  useEffect(() => {
    setCheckedGradFee(selectedEnrollment?.raw?.graduation_fee_paid === true);
  }, [selectedEnrollment?.enrollmentId, selectedEnrollment?.raw?.graduation_fee_paid]);

  // Al cambiar la matrícula seleccionada, resetear estado dependiente.
  useEffect(() => {
    setSelectedSemesterId(null);
    setExtraSemesters([]);
    setCheckedMonths([]);
    setCheckedOnDay(false);
    setAllMonthStudents([]);
  }, [selectedKey]);

  // Carga TODOS los month_students de la matrícula seleccionada (siempre, no importa si es semester-based).
  useEffect(() => {
    if (!enrollmentId || !enrollmentType) return;
    let cancelled = false;
    (async () => {
      setIsLoadingMs(true);
      try {
        const rows = await fetchMonthStudentsByEnrollment(enrollmentType, enrollmentId);
        if (!cancelled) setAllMonthStudents(rows);
      } catch (err) {
        console.error('Month load month_students:', err);
      } finally {
        if (!cancelled) setIsLoadingMs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enrollmentId, enrollmentType]);

  // Trae catálogo de semestres solo cuando se abre el modal de añadir.
  useEffect(() => {
    if (!isAddOpen || catalogSemesters.length > 0) return;
    (async () => {
      const list = await fetchAllSemesters();
      setCatalogSemesters(list);
    })();
  }, [isAddOpen, catalogSemesters.length]);

  const existingSemesters = useMemo(() => {
    const map = new Map();
    for (const ms of allMonthStudents) {
      const sem = ms.semester;
      if (sem?.id && sem?.name) map.set(sem.id, { id: sem.id, name: sem.name });
    }
    for (const s of extraSemesters) map.set(s.id, s);
    return [...map.values()].sort((a, b) => String(b.name).localeCompare(String(a.name)));
  }, [allMonthStudents, extraSemesters]);

  const derivedFromDateName = useMemo(() => deriveSemesterNameFromDate(paymentDate), [paymentDate]);

  // Precarga del semestre: preselected > derivado de fecha > semester actual del enrollment > primero existente.
  useEffect(() => {
    if (selectedSemesterId != null) return;
    if (!isSemesterBasedSelected) return;
    if (preselectedSemesterId) {
      setSelectedSemesterId(Number(preselectedSemesterId));
      return;
    }
    if (derivedFromDateName) {
      const match = existingSemesters.find((s) => String(s.name) === derivedFromDateName);
      if (match) { setSelectedSemesterId(match.id); return; }
    }
    if (selectedEnrollment?.semester?.id) {
      setSelectedSemesterId(selectedEnrollment.semester.id);
      return;
    }
    if (existingSemesters.length > 0) {
      setSelectedSemesterId(existingSemesters[0].id);
    }
  }, [existingSemesters, derivedFromDateName, preselectedSemesterId, selectedEnrollment, selectedSemesterId, isSemesterBasedSelected]);

  const currentMs = useMemo(() => {
    if (isSemesterBasedSelected) {
      if (selectedSemesterId != null) {
        const m = allMonthStudents.find((ms) => ms.semester?.id === selectedSemesterId);
        if (m) return m;

        // Distinción crítica: solo devolvemos "clean state" (null) cuando el usuario está agregando
        // un semestre nuevo alongside otro que YA existe. Si es la primera vez que asigna un
        // semestre a esta matrícula (solo hay orphan), el flujo es "promover orphan" → caemos al
        // orphan para que el usuario vea sus meses y se guarden al semestre elegido.
        const wasExplicitlyAdded = extraSemesters.some((s) => s.id === selectedSemesterId);
        const hasAnySemesterBasedMs = allMonthStudents.some((ms) => ms.semester?.id);
        if (wasExplicitlyAdded && hasAnySemesterBasedMs) return null;
      }
      // Vista inicial o promoción del orphan.
      return allMonthStudents.find((ms) => !ms.semester?.id) || null;
    }
    // No semester-based: 1 registro por matrícula (semester=null legacy).
    return allMonthStudents.find((ms) => !ms.semester?.id) || allMonthStudents[0] || null;
  }, [allMonthStudents, selectedSemesterId, isSemesterBasedSelected, extraSemesters]);

  // Si el registro actual es un orphan (sin semestre) y el usuario está por guardar para
  // un semestre concreto → estamos promoviendo el orphan a ese semestre.
  const isPromotingOrphan = !!(
    isSemesterBasedSelected &&
    currentMs &&
    !currentMs.semester?.id &&
    selectedSemesterId != null
  );

  const isTransitionalOrphan = !!(currentMs && !currentMs.semester?.id && (currentMs.months?.length ?? 0) > 0);

  useEffect(() => {
    if (currentMs) {
      const initialMonths = (currentMs.months || []).map((m) => ({ id: m.id, name: m.name }));
      setCheckedMonths(initialMonths);
      setCheckedOnDay(!!currentMs.on_day);
    } else {
      setCheckedMonths([]);
      setCheckedOnDay(false);
    }
  }, [currentMs?.id]);

  useEffect(() => { if (setMonthsParent) setMonthsParent(checkedMonths); }, [checkedMonths, setMonthsParent]);
  useEffect(() => { if (setCheckedParent) setCheckedParent(checkedOnDay); }, [checkedOnDay, setCheckedParent]);
  useEffect(() => { if (setMonthStudentParent) setMonthStudentParent(currentMs || []); }, [currentMs?.id, setMonthStudentParent]);

  useEffect(() => {
    if (!onStateChange) return;
    onStateChange({
      enrollmentId,
      enrollmentType,
      isSemesterBased: isSemesterBasedSelected,
      // Misma regla que handleSave: programas no semester-based guardan con semester=null.
      semesterId: isSemesterBasedSelected ? selectedSemesterId : null,
      months: checkedMonths,
      // Con el switch oculto o reemplazado no enviamos on_day (undefined → el upsert no lo toca).
      onDay: mode === 'on_day' ? checkedOnDay : undefined,
      graduationFeePaid: mode === 'graduation_fee' ? checkedGradFee : undefined,
      promoteOrphan: isPromotingOrphan,
    });
  }, [onStateChange, enrollmentId, enrollmentType, isSemesterBasedSelected, selectedSemesterId, checkedMonths, checkedOnDay, checkedGradFee, mode, isPromotingOrphan]);

  // Mostramos siempre los 12 meses (no filtrar por ciclo del semestre).
  const cycleMonths = MONTHS_CATALOG;

  const availableCatalogSemesters = useMemo(() => {
    const takenNames = new Set(existingSemesters.map((s) => String(s.name)));
    return catalogSemesters.filter((s) => !takenNames.has(String(s.name)));
  }, [catalogSemesters, existingSemesters]);

  const handleCheck = (item, checked) => {
    setCheckedMonths((prev) => {
      if (checked) return [...prev, { id: item.id, name: item.name }];
      return prev.filter((m) => m.id !== item.id);
    });
  };

  const handleAddSemester = (sem) => {
    setExtraSemesters((prev) => (prev.some((s) => s.id === sem.id) ? prev : [...prev, { id: sem.id, name: sem.name }]));
    setSelectedSemesterId(sem.id);
    setAddPickerValue('');
    closeAdd();
    // No hacemos reset explícito de checkedMonths / checkedOnDay: eso lo maneja el useEffect
    // de currentMs?.id, que sabe cuándo mantener los meses (promoción del orphan) y cuándo
    // limpiarlos (añadir un segundo semestre alongside).
  };

  const handleSave = async () => {
    if (!enrollmentId || !enrollmentType) return;
    if (isSemesterBasedSelected && !selectedSemesterId) {
      toasts({ title: 'Selecciona un semestre antes de guardar', status: 'warning', position: 'bottom' });
      return;
    }
    setUpdating(true);
    try {
      await saveMonths({
        studentId: idStudent,
        enrollmentType,
        enrollmentId,
        // Programas no-semester-based: se guarda con semester=null (comportamiento legacy: 1 ms por matrícula).
        semesterId: isSemesterBasedSelected ? selectedSemesterId : null,
        months: checkedMonths.map((m) => m.id),
        on_day: mode === 'on_day' ? checkedOnDay : undefined,
        promoteOrphan: isPromotingOrphan,
      });
      if (mode === 'graduation_fee') {
        await xhr.put(`/${enrollmentType.replace('_', '-')}-enrollments/${enrollmentId}`, {
          data: { graduation_fee_paid: !!checkedGradFee },
        });
      }
      toasts({ title: 'Los meses se actualizaron correctamente', status: 'success', position: 'bottom' });
      if (onSaved) onSaved();
      // No cerramos el modal al guardar — solo se cierra cuando el usuario presiona la X.
      // Esto le permite a Cindy seguir chuleando otros semestres o programas sin reabrir.
    } catch (err) {
      console.error('Month save error:', err);
      toasts({ title: 'No se pudieron actualizar los meses', status: 'error', position: 'bottom' });
    } finally {
      setUpdating(false);
    }
  };

  const isFetching = fetchingParent || isLoadingEnrollments || isLoadingMs;

  const programOptions = useMemo(() => {
    return allEnrollments
      .map((e) => ({
        key: enrollmentKey(e.type, e.enrollmentId),
        label: `${TYPE_LABEL[e.type] || e.type} — ${e.program?.name || `#${e.enrollmentId}`}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allEnrollments]);

  return (
    <Box
      marginBottom="20px"
      marginTop="10px"
      border="1px solid"
      borderColor={monthsError ? 'red.400' : '#e2e8f0'}
      borderTopRadius="5px"
    >
      <Box
        color="purple.700"
        fontWeight="500"
        paddingY="8px"
        paddingX="15px"
        backgroundColor="purple.200"
        borderTopRadius="5px"
      >
        ¿Qué meses pagó el estudiante?{showRequiredLabel ? ' *' : ''}
      </Box>

      {isFetching && allEnrollments.length === 0 ? (
        <Flex justifyContent="center" paddingY="24px">
          <Spinner />
        </Flex>
      ) : allEnrollments.length === 0 ? (
        <Box padding="15px" color="gray.600">El estudiante no tiene matrículas activas.</Box>
      ) : (
        <Box padding="15px" display="flex" flexDirection="column" gap="12px">
          {/* Selector de programa (o texto fijo si lockProgram=true) */}
          <Box>
            <Text
              fontSize="12px"
              color="gray.500"
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="0.06em"
              mb="4px"
            >
              Programa
            </Text>
            {lockProgram ? (
              <Box background="#f7f7fa" border="1px solid #e2e8f0" borderRadius="6px" padding="10px 12px">
                <Text fontSize="14px" fontWeight="600" color="gray.800">
                  {selectedEnrollment?.program?.name || program?.name || 'Programa'}
                </Text>
                <Text fontSize="12px" color="gray.500">
                  {selectedEnrollment ? `${TYPE_LABEL[selectedEnrollment.type] || selectedEnrollment.type} · matrícula ${selectedEnrollment.enrollmentId}` : ''}
                </Text>
              </Box>
            ) : (
              <>
                <Select
                  size="sm"
                  value={selectedKey ?? ''}
                  onChange={(e) => setSelectedKey(e.target.value || null)}
                >
                  {programOptions.map((op) => (
                    <option key={op.key} value={op.key}>{op.label}</option>
                  ))}
                </Select>
                {selectedEnrollment && (
                  <Text fontSize="12px" color="gray.500" mt="4px">
                    Matrícula {selectedEnrollment.enrollmentId}
                    {selectedEnrollment.status ? ` · ${getEnrollmentStatusLabel(selectedEnrollment.status)}` : ''}
                  </Text>
                )}
              </>
            )}
          </Box>

          {/* Banner transicional (solo semester-based con orphan) */}
          {isSemesterBasedSelected && isTransitionalOrphan && (
            <Box
              background="#FEF3C7"
              border="1px solid #F2C46D"
              borderRadius="6px"
              padding="10px 12px"
            >
              <Text
                fontWeight="700"
                fontSize="11px"
                letterSpacing="0.06em"
                textTransform="uppercase"
                color="#B45309"
                mb="2px"
              >
                Semestre pendiente
              </Text>
              <Text fontSize="13px" color="gray.700">
                Este registro tiene meses chuleados desde antes, pero no tiene semestre asignado. Selecciona el
                semestre correcto para completarlo.
              </Text>
            </Box>
          )}

          {/* Semestre (solo si el programa opera por semestres) */}
          {isSemesterBasedSelected && (
            <Box>
              <Text
                fontSize="12px"
                color="gray.500"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="0.06em"
                mb="4px"
              >
                Semestre
              </Text>
              <Select
                size="sm"
                value={selectedSemesterId ?? ''}
                onChange={(e) => setSelectedSemesterId(e.target.value ? Number(e.target.value) : null)}
                placeholder={existingSemesters.length === 0 ? 'Selecciona semestre…' : undefined}
              >
                {existingSemesters.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Select>
              <Button variant="ghost" size="sm" color="#574feb" mt="6px" onClick={openAdd} paddingX="4px">
                + Añadir otro semestre
              </Button>
            </Box>
          )}

          {/* Meses (siempre; para semester-based requiere elegir semestre antes) */}
          {isSemesterBasedSelected && !selectedSemesterId ? (
            <Box padding="10px" color="gray.500" fontSize="13px">
              Selecciona un semestre para ver los meses.
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gap="10px"
              padding="12px"
              border="1px solid #e2e8f0"
              borderRadius="6px"
              background="#fafafc"
            >
              {cycleMonths.map((m) => {
                const isSelected = checkedMonths.some((x) => x.id === m.id);
                return (
                  <Box key={m.id}>
                    <Checkbox
                      isChecked={isSelected}
                      onChange={(event) => handleCheck(m, event.target.checked)}
                    >
                      {m.name}
                    </Checkbox>
                  </Box>
                );
              })}
            </Box>
          )}

          {mode === 'on_day' && (
            <Flex alignItems="center" gap="10px">
              <Text fontSize="13px" color="gray.700">¿Está al día este estudiante?</Text>
              <Switch isChecked={checkedOnDay} onChange={() => setCheckedOnDay((v) => !v)} />
            </Flex>
          )}
          {mode === 'graduation_fee' && (
            <Flex alignItems="center" gap="10px">
              <Text fontSize="13px" color="gray.700">¿Está al día en derechos de grado?</Text>
              <Switch isChecked={checkedGradFee} onChange={() => setCheckedGradFee((v) => !v)} />
            </Flex>
          )}

          {insert && (
            <Flex justifyContent="flex-end">
              <Button
                bg="#574feb"
                color="white"
                _hover={{ bg: '#473fdc' }}
                size="sm"
                onClick={handleSave}
                isLoading={updating}
                isDisabled={updating || (isSemesterBasedSelected && !selectedSemesterId)}
              >
                Actualizar
              </Button>
            </Flex>
          )}
        </Box>
      )}

      {/* Modal para añadir semestre nuevo */}
      <Modal isOpen={isAddOpen} onClose={closeAdd} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Añadir semestre</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="13px" color="gray.600" mb="10px">
              Selecciona un semestre del catálogo. Solo aparecen los que aún no tiene esta matrícula.
            </Text>
            <Select
              size="sm"
              placeholder="Selecciona semestre…"
              value={addPickerValue}
              onChange={(e) => setAddPickerValue(e.target.value)}
            >
              {availableCatalogSemesters.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" size="sm" mr={2} onClick={closeAdd}>Cancelar</Button>
            <Button
              size="sm"
              bg="#574feb"
              color="white"
              _hover={{ bg: '#473fdc' }}
              onClick={() => {
                const v = Number(addPickerValue);
                if (!v) return;
                const sem = availableCatalogSemesters.find((s) => s.id === v);
                if (sem) handleAddSemester(sem);
              }}
              isDisabled={!addPickerValue}
            >
              Añadir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Month;
